import { formatCopy } from '../copy/format.js';
import copy from '../copy/en/intake.json' with { type: 'json' };
import { bindFieldHelp, createFieldHelp } from './field-help.js';
import { createPhoneField } from './phone-field.js';
import { createEmailField } from './email-field.js';
import { createSearchSelect } from './search-select.js';
import { createRangePicker } from './range-picker.js';
import { createCountField } from './count-field.js';
import { createEstimatePicker } from './estimate-picker.js';
import { scannerGroups } from './scanner-groups.js';
import { createOfficeHours, officeHoursReview } from './office-hours.js';
import { parseOfficeHours } from './office-hours-model.js';
import { bindNativeCopyActions } from './native-copy-actions.js';
import { nativeFieldError, nativeIdentityErrors } from './native-field-validation.js';
import { nativeOptionLabel } from './native-option-labels.js';
import { renderNativeReviewAnswers, type NativeReviewAnswer } from './native-review.js';
import { NATIVE_INTAKE_PROFILE_VERSION, isNativeIntakeProfileVersion, nativeIntakeFields, readNativeIntakeProfile, type NativeIntakeField, type NativeIntakeProfile, type NativeIntakeProfileVersion } from '@ohf/core/shared';

export type NativeSignerPayload = Record<string, unknown> & { signerName: string; email: string };
const nameFor = (key: string): string => `intakeProfile.${key}`;
// Presentation stays client-only: the frozen source catalogue owns validation and values.
const presentation: Record<string, { label: string; hint?: string; help?: string }> = {
  'business.legalName': { label: copy.legalBusinessName, hint: copy.useTheNameShownOnThePractice },
  'business.practiceName': { label: copy.doingBusinessAsDBA, hint: copy.addADifferentPublicPracticeNameIf },
  'locations.addressLine1': { label: copy.streetAddress, hint: copy.includeTheSuiteOrUnitHereIf },
  'locations.addressLine2': { label: copy.additionalAddressDetails },
  'locations.city': { label: copy.city },
  'locations.region': { label: copy.state },
  'locations.postalCode': { label: copy.zip },
  'locations.countryCode': { label: copy.country, hint: copy.thisAgreementSupportsUnitedStatesPractices },
  'locations.reportedLocationCount': { label: copy.numberOfLocations },
  'business.practiceType': { label: copy.practiceType },
  'business.ownershipType': { label: copy.howIsThePracticeOwned },
  'people.doctorName': { label: copy.doctorName },
  'people.officePhone': { label: copy.officePhone },
  'people.contactEmail': { label: copy.doctorEmail },
  'people.mobilePhone': { label: copy.mobilePhone },
  'people.mainContactName': { label: copy.contactName },
  'people.mainContactEmail': { label: copy.contactEmail },
  'people.jobTitle': { label: copy.contactJobTitle },
  'operations.daysOpen': { label: copy.daysOpen, hint: copy.selectEveryDayThePracticeIsOpen },
  'operations.typicalHours': { label: copy.typicalHours, hint: copy.forExampleMonThu85Fri },
  'people.fullTimeEmployeeCount': { label: copy.fullTimeEmployees, help: copy.countEmployeesWorking28HoursPerWeek },
  'people.ownerDentistCount': { label: copy.ownerDentists },
  'people.dentistCount': { label: copy.dentists },
  'people.hygienistCount': { label: copy.hygienists },
  'operations.operatories': { label: copy.operatories, help: copy.operatoriesHelp },
  'operations.monthlyPatientVisits': { label: copy.approximateMonthlyPatientVisits },
  'operations.activePatientCount': { label: copy.approximateActivePatients, help: copy.activePatientsHelp },
  'operations.practiceSoftware': { label: copy.practiceSoftware },
  'operations.intraoralScanner': { label: copy.intraoralScanner },
  'business.entityType': { label: copy.entityType, help: copy.entityTypeHelp },
  'business.yearsInPractice': { label: copy.yearsInPractice },
  'business.npi': { label: copy.npiNumber, help: copy.npiHelp },
  'business.website': { label: copy.website },
  'financial.practiceRevenue': { label: copy.revenueLastYear },
  'financial.annualCardVolume': { label: copy.approximateAnnualCardVolume, help: copy.annualCardVolumeHelp },
  'financial.processingFee': { label: copy.creditCardProcessingFee, help: copy.processingFeeHelp },
  'business.facilityTenure': { label: copy.doYouOwnOrLeaseYourFacility },
  'interests.acquisitionIntent': { label: copy.anticipatingAcquiringAnotherPractice },
  'interests.transitionIntent': { label: copy.consideringSellingOrTransitioningWithinTheNext },
  'operations.procedures': { label: copy.proceduresOffered, hint: copy.selectAllProceduresYourPracticeCurrentlyProvides },
  'operations.insuranceCarriers': { label: copy.insuranceAccepted, hint: copy.selectAllInsuranceCompaniesYourPracticeAccepts },
};
const labelFor = (field: NativeIntakeField): string => presentation[field.key]?.label ?? field.label;
const inputPlaceholders: Record<string, string> = {
  'practiceName': copy.placeholderPracticeName,
  'business.legalName': copy.placeholderBusinessLegalName,
  'locations.addressLine1': copy.placeholderLocationsAddressLine1,
  'locations.city': copy.placeholderLocationsCity,
  'locations.region': copy.placeholderLocationsRegion,
  'locations.postalCode': copy.placeholderLocationsPostalCode,
  'locations.countryCode': copy.placeholderLocationsCountryCode,
  'locations.reportedLocationCount': copy.placeholderLocationsReportedLocationCount,
  'people.doctorName': copy.placeholderPeopleDoctorName,
  'people.contactEmail': copy.placeholderPeopleContactEmail,
  'people.officePhone': copy.placeholderPeopleOfficePhone,
  'people.mobilePhone': copy.placeholderPeopleMobilePhone,
  'people.mainContactName': copy.placeholderPeopleMainContactName,
  'people.mainContactEmail': copy.placeholderPeopleMainContactEmail,
  'people.jobTitle': copy.placeholderPeopleJobTitle,
  'signerName': copy.placeholderSignerName,
  'email': copy.placeholderEmail,
  'signerTitle': copy.placeholderSignerTitle,
  'people.fullTimeEmployeeCount': copy.placeholderPeopleFullTimeEmployeeCount,
  'people.ownerDentistCount': copy.placeholderPeopleOwnerDentistCount,
  'people.dentistCount': copy.placeholderPeopleDentistCount,
  'people.hygienistCount': copy.placeholderPeopleHygienistCount,
  'operations.operatories': copy.placeholderOperationsOperatories,
  'operations.activePatientCount': copy.placeholderOperationsActivePatientCount,
  'business.yearsInPractice': copy.placeholderBusinessYearsInPractice,
  'business.npi': copy.placeholderBusinessNpi,
  'business.website': copy.placeholderBusinessWebsite,
};
const placeholderFor = (key: string): string => inputPlaceholders[key] ?? '';
const radioKeys = new Set(['business.ownershipType', 'business.facilityTenure', 'interests.acquisitionIntent', 'interests.transitionIntent']);
const rangeKeys = new Set(['financial.practiceRevenue', 'financial.annualCardVolume', 'financial.processingFee']);
const isPopupMulti = (field: NativeIntakeField): boolean => field.kind === 'multi' && ['operations.intraoralScanner', 'operations.insuranceCarriers'].includes(field.key);
const openDaysKey = 'operations.daysOpen';
const openDayCountKey = 'operations.reportedOpenDaysPerWeek';
const isEditable = (field: NativeIntakeField): boolean => !field.hidden && !['locations.addressLine2', 'business.practiceName', openDayCountKey].includes(field.key);
const retainedAnswers = new WeakMap<HTMLFormElement, NativeIntakeProfile['answers']>();
const retainedCapacityIdentity = new WeakMap<HTMLFormElement, string>();
const capacityIdentity = (data: Record<string, unknown>): string => JSON.stringify(['signerName', 'email', 'signerTitle'].map(key => String(data[key] ?? '').trim()));
const selectedOpenDays = (form: HTMLFormElement, values: FormData): Set<string> => {
  const allowed = nativeIntakeFields(formVersion(form)).find(field => field.key === openDaysKey)!.options;
  return new Set(values.getAll(nameFor(openDaysKey)).map(String).filter(day => allowed.includes(day)));
};
const formVersion = (form: HTMLFormElement): NativeIntakeProfileVersion => {
  const version = form.dataset.intakeProfileVersion;
  if (!isNativeIntakeProfileVersion(version)) throw new Error('Unsupported intake profile');
  return version;
};
const resumedAddresses = new WeakMap<HTMLFormElement, { line1?: string; line2: string; presented: string }>();
const helpDisposers = new WeakMap<HTMLFormElement, () => void>();
const copyActions = new WeakMap<HTMLFormElement, ReturnType<typeof bindNativeCopyActions>>();
const phoneControls = new WeakMap<HTMLInputElement, ReturnType<typeof createPhoneField>>();
const emailControls = new WeakMap<HTMLFormElement, ReturnType<typeof createEmailField>[]>();
const searchSelectControls = new WeakMap<HTMLFormElement, ReturnType<typeof createSearchSelect>[]>();
const officeHoursControls = new WeakMap<HTMLFormElement, ReturnType<typeof createOfficeHours>>();
const countControls = new WeakMap<HTMLInputElement, ReturnType<typeof createCountField>>();
const countUnits: Record<string, readonly [string, string]> = {
  'people.fullTimeEmployeeCount': [copy.countPerson, copy.countPeople],
  'people.ownerDentistCount': [copy.countPerson, copy.countPeople],
  'people.dentistCount': [copy.countPerson, copy.countPeople],
  'people.hygienistCount': [copy.countPerson, copy.countPeople],
  'operations.operatories': [copy.countRoom, copy.countRooms],
  'operations.activePatientCount': [copy.countPatient, copy.countPatients],
  'locations.reportedLocationCount': [copy.countLocation, copy.countLocations],
  'business.yearsInPractice': [copy.countYear, copy.countYears],
};
const create = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
};

function fieldShell(name: string, label: string, required: boolean, help?: string, asCard = false): HTMLElement {
  const wrap = create(asCard ? 'fieldset' : 'div', `field native-field${asCard ? ' field-group' : ''}${required ? ' is-required' : ''}`);
  const heading = create(asCard ? 'legend' : 'div', asCard ? undefined : 'field-heading');
  const caption = create('label', asCard ? undefined : 'field-label', label); caption.htmlFor = `field-${name}`;
  heading.append(caption);
  if (help) heading.append(createFieldHelp({ id: `${name}-help`, text: help, buttonLabel: formatCopy(copy.fieldHelpLabel, { field: label }) }));
  if (!required) heading.append(create('small', 'optional', copy.optional));
  wrap.append(heading);
  return wrap;
}

function configureNpiInput(input: HTMLInputElement): void {
  input.inputMode = 'numeric';
  input.minLength = input.maxLength = 10;
  input.pattern = '[0-9]{10}';
  input.addEventListener('input', () => {
    const original = input.value;
    const digits = original.replace(/[^0-9]/g, '').slice(0, 10);
    if (digits === original) return;
    const start = original.slice(0, input.selectionStart ?? original.length).replace(/[^0-9]/g, '').length;
    const end = original.slice(0, input.selectionEnd ?? original.length).replace(/[^0-9]/g, '').length;
    input.value = digits;
    input.setSelectionRange(Math.min(start, 10), Math.min(end, 10));
  });
  input.addEventListener('paste', event => {
    if (!event.clipboardData) return;
    event.preventDefault();
    const digits = event.clipboardData.getData('text/plain').replace(/[^0-9]/g, '');
    if (!digits) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const room = Math.max(0, 10 - (input.value.length - (end - start)));
    input.setRangeText(digits.slice(0, room), start, end, 'end');
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

function control(field: NativeIntakeField): HTMLElement {
  const name = nameFor(field.key);
  const singleChoice = radioKeys.has(field.key);
  const popupMulti = isPopupMulti(field);
  const grouped = field.kind === 'multi' && !popupMulti || singleChoice;
  const helpText = presentation[field.key]?.help;
  const wrap = grouped
    ? create('fieldset', singleChoice ? 'choice-group seg' : 'field-group native-multi')
    : fieldShell(name, labelFor(field), field.required, helpText, field.key === 'operations.insuranceCarriers');
  if (grouped && field.required) wrap.classList.add('is-required');
  if (grouped) wrap.append(create('legend', undefined, labelFor(field)));
  if (grouped && !field.required) wrap.append(create('small', 'hint', copy.optional));
  const hint = presentation[field.key]?.hint;
  const hintId = `${name}-hint`;
  const help = hint ? create('small', 'hint', hint) : undefined;
  if (help) help.id = hintId;
  if (singleChoice) {
    if (help) { wrap.append(help); wrap.setAttribute('aria-describedby', hintId); }
    const choices = create('div', 'choice-row');
    choices.setAttribute('role', 'radiogroup');
    choices.setAttribute('aria-label', labelFor(field));
    if (field.required) choices.setAttribute('aria-required', 'true');
    for (const option of field.options) {
      const label = create('label', 'choice');
      const input = create('input'); input.type = 'radio'; input.name = name; input.value = option; input.required = field.required;
      label.append(input, create('span', undefined, option)); choices.append(label);
    }
    wrap.append(choices);
  } else if (field.kind === 'multi' && !popupMulti) {
    if (help) { wrap.append(help); wrap.setAttribute('aria-describedby', hintId); }
    const choices = create('div', 'native-checkboxes');
    for (const option of field.options) {
      const label = create('label', 'native-checkbox');
      const input = create('input'); input.type = 'checkbox'; input.name = name; input.value = option;
      label.append(input, create('span', undefined, option)); choices.append(label);
    }
    wrap.append(choices);
  } else {
    const input = field.options.length ? create('select') : create('input');
    input.name = name; input.id = `field-${name}`; input.required = field.required;
    const descriptions = [hint ? hintId : '', helpText ? `${name}-help` : ''].filter(Boolean);
    if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
    if (input instanceof HTMLSelectElement) {
      input.multiple = popupMulti;
      if (!popupMulti) input.append(new Option(rangeKeys.has(field.key) ? copy.financialRangePlaceholder : copy.chooseOne, ''));
      const options = field.key === 'operations.practiceSoftware'
        ? [...field.options].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
        : field.options;
      for (const option of options) input.append(new Option(nativeOptionLabel(field.key, option), option));
    } else {
      input.type = field.kind === 'number' ? 'number' : field.kind === 'email' ? 'email' : field.kind === 'tel' ? 'tel' : 'text';
      input.placeholder = placeholderFor(field.key);
      input.maxLength = 4000;
      if (field.kind === 'number') { input.min = '0'; input.step = '1'; }
      if (field.key === 'locations.region') input.maxLength = 2;
      if (field.key === 'business.npi') configureNpiInput(input);
      if (field.key === 'business.website') { input.inputMode = 'url'; input.setAttribute('autocomplete', 'url'); }
    }
    if (input instanceof HTMLInputElement && field.numberConstraints && countUnits[field.key]) {
      const units = countUnits[field.key]!;
      const options: Parameters<typeof createCountField>[1] = {
        ...field.numberConstraints,
        unit: value => units[value && Number(value) === 1 ? 0 : 1],
        decreaseLabel: formatCopy(copy.decreaseCount, { field: labelFor(field) }),
        increaseLabel: formatCopy(copy.increaseCount, { field: labelFor(field) }),
        invalidMessage: copy.countWholeNumber,
        minimumMessage: formatCopy(copy.countMinimum, { minimum: field.numberConstraints.min }),
        maximumMessage: copy.countTooLarge,
      };
      const count = field.key === 'operations.activePatientCount' ? createEstimatePicker(input, {
        ...options,
        sliderLabel: copy.activePatientEstimateSlider,
        unansweredText: copy.activePatientEstimateUnanswered,
        formatValue: value => new Intl.NumberFormat('en-US').format(value),
      }) : createCountField(input, options);
      countControls.set(input, count); wrap.append(count.element);
    } else if (input instanceof HTMLInputElement && field.kind === 'tel') {
      const phone = createPhoneField(input, { countryLabel: formatCopy(copy.phoneCountryLabel, { field: labelFor(field) }) });
      phoneControls.set(input, phone);
      wrap.append(phone.element);
    } else wrap.append(input);
    if (help) wrap.append(help);
  }
  const error = create('em', 'err'); error.dataset.err = name; wrap.append(error);
  return wrap;
}

function agreementField(name: string, label: string, type = 'text', help?: string): HTMLElement {
  const wrap = fieldShell(name, label, true, help);
  const input = create('input'); input.name = name; input.id = `field-${name}`; input.type = type; input.required = true;
  input.placeholder = placeholderFor(name);
  if (help) input.setAttribute('aria-describedby', `${name}-help`);
  const error = create('em', 'err'); error.dataset.err = name;
  wrap.append(input, error); return wrap;
}

function row(className: string, ...fields: HTMLElement[]): HTMLElement {
  const element = create('div', className); element.append(...fields); return element;
}

function card(title: string, fields: HTMLElement[], intro?: string): HTMLElement {
  const element = create('fieldset', 'field-group'); element.append(create('legend', undefined, title));
  if (intro) element.append(create('p', 'group-intro', intro));
  element.append(row('field-stack', ...fields)); return element;
}

function practiceNameField(): HTMLElement {
  const field = agreementField('practiceName', copy.practiceName);
  const hint = create('small', 'hint', copy.practicePublicNameHint);
  hint.id = 'practiceName-hint';
  field.querySelector('input')!.setAttribute('aria-describedby', hint.id);
  field.insertBefore(hint, field.querySelector('.err'));
  return field;
}

function retainedCapacityField(): HTMLInputElement {
  // Keep an older draft's recorded evidence without asking for it or inferring authority.
  const input = create('input'); input.type = 'hidden'; input.name = 'signerCapacity';
  return input;
}

function stageCards(index: number, fields: readonly NativeIntakeField[], form: HTMLFormElement): HTMLElement[] {
  const field = (key: string): HTMLElement => {
    const definition = fields.find(field => field.key === key && field.stage === index && isEditable(field));
    if (!definition) throw new Error(`Missing native intake presentation field: ${key}`);
    return control(definition);
  };
  const pair = (...keys: string[]): HTMLElement => row('field-pair', ...keys.map(field));
  switch (index) {
    case 0: return [
      card(copy.practiceIdentity, [practiceNameField(), field('business.legalName')], copy.thePracticeNameAndLegalBusinessName),
      card(copy.primaryLocation, [
        field('locations.addressLine1'),
        row('field-pair address-pair', field('locations.city'), row('field-pair compact-pair', field('locations.region'), field('locations.postalCode'))),
        field('locations.countryCode'), pair('locations.reportedLocationCount', 'business.practiceType'),
      ]),
      card(copy.ownership, [field('business.ownershipType')]),
    ];
    case 1: return [
      card(copy.doctorContact, [field('people.doctorName'), field('people.contactEmail'), pair('people.officePhone', 'people.mobilePhone')]),
      card(copy.mainPracticeContact, [pair('people.mainContactName', 'people.jobTitle'), field('people.mainContactEmail')]),
      card(copy.authorizedSigner, [
        row('field-pair', agreementField('signerName', copy.signerLegalName), agreementField('signerTitle', copy.signerJobTitle, 'text', copy.signerJobTitleHelp)),
        agreementField('email', copy.signerEmail, 'email'),
        retainedCapacityField(),
      ], copy.theAuthorizedSignerMayDifferFromThe),
    ];
    case 2: return [
      officeHoursControls.get(form)!.element,
      card(copy.team, [pair('people.fullTimeEmployeeCount', 'people.ownerDentistCount'), pair('people.dentistCount', 'people.hygienistCount')]),
      card(copy.patientsCapacity, [field('operations.activePatientCount'), pair('operations.monthlyPatientVisits', 'operations.operatories')]),
      card(copy.technology, [field('operations.practiceSoftware'), field('operations.intraoralScanner')]),
    ];
    case 3: return [
      card(copy.businessDetails, [pair('business.entityType', 'business.yearsInPractice'), pair('business.npi', 'business.website')]),
      card(copy.financialSnapshot, [row('financial-range-grid', field('financial.practiceRevenue'), field('financial.processingFee')), field('financial.annualCardVolume')]),
      card(copy.facilityPlans, [field('business.facilityTenure'), field('interests.acquisitionIntent'), field('interests.transitionIntent')]),
    ];
    case 4: return [field('operations.procedures'), field('operations.insuranceCarriers')];
    default: return [];
  }
}

/** Session metadata, not current document metadata or a payload flag, chooses this UI. */
export function configureNativeIntake(form: HTMLFormElement, version: NativeIntakeProfileVersion = NATIVE_INTAKE_PROFILE_VERSION): void {
  if (!isNativeIntakeProfileVersion(version)) throw new Error('Unsupported intake profile');
  if (form.dataset.intakeProfileVersion === version) return;
  disposeNativeIntake(form);
  form.dataset.intakeProfileVersion = version;
  officeHoursControls.set(form, createOfficeHours(form));
  const titles = [copy.practiceDetails, copy.peopleContacts, copy.practiceOperations, copy.businessProfile, copy.servicesOffered];
  const intros = [
    copy.startWithYourPracticeAndItsPrimary,
    copy.tellUsWhoToContactAndWho,
    copy.tellUsAboutYourTeamScheduleAnd,
    copy.tellUsAboutTheBusinessTodayAnd,
    copy.selectTheProceduresYouProvideAndThe,
  ];
  for (const [index, stage] of Array.from(form.querySelectorAll<HTMLElement>('[data-data-stage]')).entries()) {
    for (const child of Array.from(stage.children)) {
      if (!child.matches('.stage-header, .stage-actions')) child.remove();
    }
    const header = stage.querySelector('.stage-header')!;
    header.querySelector('h2')!.textContent = titles[index]!;
    header.querySelector('p:last-child')!.textContent = intros[index]!;
    const content = create('div', 'field-stack native-intake-fields');
    content.append(...stageCards(index, nativeIntakeFields(version), form));
    stage.insertBefore(content, stage.querySelector('.stage-actions'));
  }
  copyActions.set(form, bindNativeCopyActions(form));
  helpDisposers.set(form, bindFieldHelp(form));
  emailControls.set(form, Array.from(form.querySelectorAll<HTMLInputElement>('input[type="email"]'), input => {
    input.autocomplete = 'email'; input.inputMode = 'email';
    return createEmailField(input, { suggestionsLabel: copy.emailSuggestions });
  }));
  searchSelectControls.set(form, nativeIntakeFields(version)
    .filter(field => isEditable(field) && (field.kind === 'select' && !radioKeys.has(field.key) || isPopupMulti(field)))
    .map(field => {
      const select = form.elements.namedItem(nameFor(field.key)) as HTMLSelectElement;
      const options: Parameters<typeof createSearchSelect>[1] = {
      label: labelFor(field),
      searchable: isPopupMulti(field) || field.options.length >= 10 && !['operations.monthlyPatientVisits', 'financial.processingFee'].includes(field.key),
      searchLabel: formatCopy(copy.searchChoicesLabel, { field: labelFor(field) }),
      emptyText: copy.noMatchingChoices,
      resultsText: (shown, total) => formatCopy(copy.limitedChoiceResults, { shown, total }),
      ...(field.kind === 'multi' && field.key === 'operations.intraoralScanner' ? {
        groups: scannerGroups(field.options, field.exclusiveOptions ?? [], copy.scannerStatus, value => nativeOptionLabel(field.key, value)),
        exclusiveValues: field.exclusiveOptions,
        selectedText: (count: number) => count ? formatCopy(copy.scannersSelected, { count }) : copy.chooseScanners,
        removeLabel: (label: string) => formatCopy(copy.removeScanner, { model: label }),
        doneLabel: copy.scannerDone,
      } : field.kind === 'multi' ? {
        groups: [{ label: copy.insuranceCarriers, options: [...field.options]
          .sort((a, b) => Number(a === 'Other') - Number(b === 'Other') || a.localeCompare(b, 'en', { sensitivity: 'base' }))
          .map(value => ({ value })) }],
        selectedText: (count: number) => count ? formatCopy(copy.insuranceSelected, { count }) : copy.chooseInsurance,
        removeLabel: (label: string) => formatCopy(copy.removeInsurance, { carrier: label }),
        doneLabel: copy.insuranceDone,
      } : {}),
      };
      return rangeKeys.has(field.key) ? createRangePicker(select, {
        search: options,
        decreaseLabel: formatCopy(copy.previousFinancialRange, { field: labelFor(field) }),
        increaseLabel: formatCopy(copy.nextFinancialRange, { field: labelFor(field) }),
        sliderLabel: formatCopy(copy.financialRangeSlider, { field: labelFor(field) }),
        unansweredText: copy.financialRangeUnanswered,
      }) : createSearchSelect(select, options);
    }));
}

export function disposeNativeIntake(form: HTMLFormElement): void {
  retainedCapacityIdentity.delete(form);
  copyActions.get(form)?.dispose();
  copyActions.delete(form);
  officeHoursControls.get(form)?.dispose();
  officeHoursControls.delete(form);
  helpDisposers.get(form)?.();
  helpDisposers.delete(form);
  emailControls.get(form)?.forEach(control => control.dispose());
  emailControls.delete(form);
  searchSelectControls.get(form)?.forEach(control => control.dispose());
  searchSelectControls.delete(form);
  for (const input of form.querySelectorAll<HTMLInputElement>('.count-field input')) {
    countControls.get(input)?.dispose(); countControls.delete(input);
  }
  for (const input of form.querySelectorAll<HTMLInputElement>('input[type="tel"]')) {
    phoneControls.get(input)?.dispose();
    phoneControls.delete(input);
  }
}

export function collectNativeIntake(form: HTMLFormElement): NativeSignerPayload {
  const version = formVersion(form);
  const values = new FormData(form);
  const answers: NativeIntakeProfile['answers'] = { ...retainedAnswers.get(form) };
  for (const field of nativeIntakeFields(version)) {
    if (!isEditable(field)) continue;
    const input = form.elements.namedItem(nameFor(field.key));
    const phone = input instanceof HTMLInputElement ? phoneControls.get(input) : undefined;
    const entries = phone ? [phone.getValue()] : values.getAll(nameFor(field.key)).map(String);
    if (field.kind === 'multi') { if (entries.length) answers[field.key] = entries; }
    else if (entries[0]?.trim()) answers[field.key] = entries[0].trim();
  }
  // Keep the payload count aligned with the selected valid days.
  delete answers[openDayCountKey];
  const days = selectedOpenDays(form, values);
  if (days.size) answers[openDayCountKey] = String(days.size);
  const resumedAddress = resumedAddresses.get(form);
  if (resumedAddress) {
    if (String(values.get(nameFor('locations.addressLine1')) ?? '').trim() === resumedAddress.presented.trim()) {
      if (resumedAddress.line1 !== undefined) answers['locations.addressLine1'] = resumedAddress.line1;
      else delete answers['locations.addressLine1'];
      answers['locations.addressLine2'] = resumedAddress.line2;
    } else {
      // Editing the combined street explicitly replaces both old address lines.
      delete answers['locations.addressLine2'];
    }
  }
  const scalar = (key: string): string => typeof answers[key] === 'string' ? answers[key] as string : '';
  const data: NativeSignerPayload = {
    signerName: String(values.get('signerName') ?? '').trim(), email: String(values.get('email') ?? '').trim(),
    signerTitle: String(values.get('signerTitle') ?? '').trim(), signerCapacity: String(values.get('signerCapacity') ?? '').trim(),
    practiceName: String(values.get('practiceName') ?? '').trim(), legalCompanyName: scalar('business.legalName'),
    phone: scalar('people.officePhone'), addressStreet: [scalar('locations.addressLine1'), scalar('locations.addressLine2')].filter(Boolean).join(', '), city: scalar('locations.city'),
    state: scalar('locations.region').toUpperCase(), zip: scalar('locations.postalCode'),
    intakeProfile: { schemaVersion: version, answers },
  };
  // A prior authority statement must not be attributed to an edited signer or role.
  const originalIdentity = retainedCapacityIdentity.get(form);
  if (originalIdentity !== undefined && capacityIdentity(data) !== originalIdentity) data.signerCapacity = '';
  return data;
}

export function prefillNativeIntake(form: HTMLFormElement, raw: Record<string, unknown>): void {
  const version = formVersion(form);
  const profile = readNativeIntakeProfile(raw.intakeProfile);
  if (profile && profile.schemaVersion !== version) throw new Error('Intake profile does not match this session');
  // Index once: repeatedly scanning every country option makes restoration expensive.
  const controls = new Map<string, (HTMLInputElement | HTMLSelectElement)[]>();
  for (const input of form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input[name], select[name]')) {
    const named = controls.get(input.name) ?? [];
    named.push(input);
    controls.set(input.name, named);
  }
  // A restored partial snapshot replaces the displayed answers, including blanks.
  for (const field of nativeIntakeFields(version).filter(isEditable)) {
    for (const input of controls.get(nameFor(field.key)) ?? []) {
      if (input instanceof HTMLInputElement && ['radio', 'checkbox'].includes(input.type)) input.checked = false;
      else if (input instanceof HTMLSelectElement && input.multiple) {
        for (const option of input.options) option.selected = false;
      } else {
        const phone = input instanceof HTMLInputElement ? phoneControls.get(input) : undefined;
        if (phone) phone.setValue('');
        else input.value = '';
      }
    }
  }
  for (const name of ['practiceName', 'signerName', 'email', 'signerTitle', 'signerCapacity']) {
    const input = controls.get(name)?.[0];
    if (input instanceof HTMLInputElement) input.value = '';
  }
  resumedAddresses.delete(form);
  retainedAnswers.set(form, profile ? Object.fromEntries(nativeIntakeFields(version).filter(field => !isEditable(field) && field.key !== openDayCountKey && profile.answers[field.key] !== undefined).map(field => [field.key, profile.answers[field.key]])) : {});
  if (profile) for (const [key, value] of Object.entries(profile.answers)) {
    for (const input of controls.get(nameFor(key)) ?? []) {
      if (input instanceof HTMLInputElement && input.type === 'checkbox') input.checked = Array.isArray(value) && value.includes(input.value);
      else if (input instanceof HTMLInputElement && input.type === 'radio') input.checked = value === input.value;
      else if (input instanceof HTMLSelectElement && input.multiple) {
        for (const option of input.options) option.selected = Array.isArray(value) && value.includes(option.value);
      } else if (typeof value === 'string') {
        const phone = input instanceof HTMLInputElement ? phoneControls.get(input) : undefined;
        if (phone) phone.setValue(value);
        else input.value = value;
      }
      if (typeof value === 'string' && value.trim()) {
        const disclosure = input.closest('details');
        if (disclosure) disclosure.open = true;
      }
    }
  }
  const line2 = profile?.answers['locations.addressLine2'];
  const street = controls.get(nameFor('locations.addressLine1'))?.[0];
  if (typeof line2 === 'string' && street instanceof HTMLInputElement) {
    const line1 = profile?.answers['locations.addressLine1'];
    const originalLine1 = typeof line1 === 'string' ? line1 : undefined;
    const presented = [originalLine1, line2].filter(Boolean).join(', ');
    resumedAddresses.set(form, { line1: originalLine1, line2, presented });
    street.value = presented;
  }
  for (const name of ['practiceName', 'signerName', 'email', 'signerTitle', 'signerCapacity']) {
    const input = controls.get(name)?.[0];
    if (input instanceof HTMLInputElement && typeof raw[name] === 'string') input.value = raw[name];
  }
  retainedCapacityIdentity.set(form, capacityIdentity(raw));
  searchSelectControls.get(form)?.forEach(control => control.sync());
  for (const input of form.querySelectorAll<HTMLInputElement>('.count-field input')) countControls.get(input)?.sync();
  officeHoursControls.get(form)?.restore(profile?.answers['operations.daysOpen'], profile?.answers['operations.typicalHours']);
  copyActions.get(form)?.sync();
}

export function nativeStageErrors(stage: HTMLElement): Record<string, string> {
  const errors: Record<string, string> = {};
  const form = stage.closest('form')!;
  // Validate the actual serialized values, including international phone numbers
  // and a resumed combined address, rather than their formatted display text.
  const data = collectNativeIntake(form);
  const profile = data.intakeProfile as NativeIntakeProfile;
  const available = (name: string): boolean => {
    const input = stage.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
    return Boolean(input && !input.disabled);
  };
  for (const field of nativeIntakeFields(formVersion(form))) {
    if (!available(nameFor(field.key))) continue;
    const error = nativeFieldError(field, profile.answers[field.key]);
    if (error) errors[nameFor(field.key)] = error;
  }
  for (const [name, error] of Object.entries(nativeIdentityErrors(data))) {
    if (available(name)) errors[name] = error;
  }
  for (const input of stage.querySelectorAll<HTMLInputElement>('.count-field input')) {
    const count = countControls.get(input); count?.sync();
    if (count?.error()) errors[input.name] = count.error();
  }
  if (stage.querySelector('[data-office-hours]')) Object.assign(errors, officeHoursControls.get(form)?.validate());
  return errors;
}

/** Maps the persisted scalar's errors to the precise visible editor control. */
export function nativeErrorControl(form: HTMLFormElement, name: string): HTMLElement | undefined {
  return officeHoursControls.get(form)?.errorControl(name);
}

export function renderNativeReview(target: HTMLElement, data: NativeSignerPayload): void {
  const profile = readNativeIntakeProfile(data.intakeProfile);
  const answers: NativeReviewAnswer[] = [
    { key: 'practiceName', stage: 0, label: copy.practiceNameForTheAgreement, required: true, value: data.practiceName },
    ...[['signerName', copy.signerLegalName], ['email', copy.authorizedSignerEmail], ['signerTitle', copy.signerJobTitle]].map(([key, label]) => ({ key: key!, stage: 1, label: label!, required: true, value: data[key!] })),
    { key: 'signerCapacity', stage: 1, label: copy.signingCapacity, required: false, value: data.signerCapacity },
    ...nativeIntakeFields(profile?.schemaVersion ?? NATIVE_INTAKE_PROFILE_VERSION)
      .filter(field => !field.hidden && field.key !== openDayCountKey)
      .map(field => {
        const value = profile?.answers[field.key];
        return {
          key: field.key, stage: field.stage, required: field.required,
          label: field.key === 'people.fullTimeEmployeeCount' ? copy.fullTimeEmployees28HoursWeek : labelFor(field),
          value: field.key === 'operations.typicalHours' && typeof value === 'string' && parseOfficeHours(value)
            ? officeHoursReview(value).split('; ').join('\n')
            : Array.isArray(value) ? value.map(item => nativeOptionLabel(field.key, item))
            : typeof value === 'string' ? nativeOptionLabel(field.key, value) : value,
        };
      }),
  ];
  renderNativeReviewAnswers(target, answers);
}
